import React, {useEffect, useState} from "react";
import {
  Button,
  Col,
  FormGroup,
  Input,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import PlacesAutocomplete from "react-places-autocomplete";
import {parseGoogleAddress} from "../../utils/addressUtils";
import {isEmpty, every} from "lodash";
import PropTypes from "prop-types";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {toast} from "react-toastify";
import {_Buttons} from "../../constants/appConstants";
import {requestUserList} from "../../redux/Actions/group.action";
import {siteModalAction, updateSelectedLocation} from "../../redux/Actions/sites.actions";
import {getUsersList} from "../../redux/Selectors/group.selector";
import {
  getCurrentSite,
  getSelectedLocations,
} from "../../redux/Selectors/sites.selectors";
import * as actions from "../../redux/action.constants";
import ZeusButton from "../Button";
import InputCreator from "../Forms/components/InputCreator";
import SelectCreator from "../Forms/components/SelectCreator";
import ToggleCreator from "../Forms/components/ToggleCreator";
import Loader from "../Loader";
import ProtectedContent, {usePermissionCheck} from "../protectedContent";
import AsteriskIcon from "../asteriskIcon";
import {countryList} from "../../constants/countryList";
import "./createSite.scss";

const MANDATORY_KEYS = [
  "name",
  "street_address1",
  "city",
  "state",
  "country",
  "postal_code",
];

const initialAddress = {
  street_address1: "",
  city: "",
  state: "",
  country: "",
  postal_code: "",
  latitude: 0.0,
  longitude: 0.0,
};

const CreateSite = (props) => {
  const {data, onClose, open, type} = props;
  const isSiteForm = type === "site";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const currentSiteData = useSelector(getCurrentSite);
  const user_options = useSelector(getUsersList);
  const selectedLocation = useSelector(getSelectedLocations);
  let siteData = currentSiteData || {};
  let {id: siteId = ""} = siteData;
  const [formData, setFormPayload] = useState(data || {});
  let {
    id = "",
    name = "",
    is_active = false,
    users = [],
    address = initialAddress,
  } = formData;
  const {street_address1, city, state, country, postal_code} = address;
  const isEdit = !!formData.id;

  const canViewUsers = usePermissionCheck(["view_user"]);

  useEffect(() => {
    if (canViewUsers) {
      dispatch(requestUserList());
    }
  }, [canViewUsers, dispatch]);

  const checkForValidation = () => {
    setSubmitted(true);
    const validated = every(
      MANDATORY_KEYS,
      (each) => !!formData[each] || !!formData?.address?.[each]
    );
    if (!validated) {
      toast.error("Please check required fields");
    }
    return validated;
  };

  const handleSubmit = () => {
    if (!checkForValidation()) {
      return;
    }

    const action = isSiteForm
      ? actions.SEND_SITE_MODAL_DATA
      : actions.SEND_LOCATION_MODAL_DATA;

    const {address, is_active, ...rest} = formData;
    const country_code = countryList.find(
      (eachCountry) => eachCountry.value === country
    )?.code;
    const payload = {
      ...rest,
      address: {...address, country_code},
      is_active: !!is_active,
    };

    if (formData.id) {
      dispatch(siteModalAction(payload, action, "patch", siteId, onClose));
    } else {
      dispatch(siteModalAction(payload, action, "post", siteId, onClose));
    }
  };

  const handleDelete = () => {
    const action = isSiteForm
      ? actions.SEND_SITE_MODAL_DATA
      : actions.SEND_LOCATION_MODAL_DATA;

    const callback = (payload) => {
      onClose();
      if (isSiteForm) {
        navigate("/app/customer");
      } else {
        if (selectedLocation.id === payload.id) {
          dispatch(updateSelectedLocation(undefined));
        }
      }
    };
    dispatch(siteModalAction(formData, action, "delete", siteId, callback));
  };

  const handleSelectAddress = async (address) => {
    const parsed = await parseGoogleAddress(address);
    if (!parsed) return;
    const {country, state, city, postalCode, streetAddress, latitude, longitude} = parsed;
    setFormPayload((prev) => ({
      ...prev,
      address: {
        ...(prev.address || initialAddress),
        country,
        state,
        city,
        latitude,
        longitude,
        postal_code: postalCode,
        street_address1: streetAddress,
      },
    }));
  };

  const handleChange = (e, type) => {
    const {is_active = false} = formData;
    let updateObj = {...formData};
    if (type === 1) {
      updateObj = Object.assign({}, updateObj, {[e.target.name]: e.target.value});
    } else if (type === 2) {
      updateObj = Object.assign({}, updateObj, {[e.target.name]: !is_active});
    } else if (type === 3) {
      updateObj = Object.assign({}, updateObj, {
        address: {
          ...(updateObj.address || initialAddress),
          [e.target.name]: e.target.value,
        },
      });
    } else if (type === 4) {
      updateObj = Object.assign({}, updateObj, {
        ["users"]: e.target.value,
      });
    }
    setFormPayload(updateObj);
  };

  return (
    <Modal
      data-cy={"select_sites_dropdown"}
      unmountOnClose={true}
      isOpen={open}
      backdrop={"static"}
    >
      <Loader />
      <ModalHeader>
        {isSiteForm
          ? isEdit
            ? "Edit Site"
            : "Add New Site"
          : isEdit
            ? "Edit Location"
            : "Add New Location"}
      </ModalHeader>
      <ModalBody>
        <div className="pl-lg-4">
          <Row>
            <InputCreator
              large
              label={isSiteForm ? "Site Name" : "Location Name"}
              className="form-control-alternative"
              data-cy="site-name"
              placeholder={isSiteForm ? "Enter Site Name" : "Enter Location Name"}
              type="text"
              name={"name"}
              value={name}
              onChange={(e) => handleChange(e, 1)}
              isRequired
              invalid={isEmpty(name) && submitted}
            />
            <ToggleCreator
              label={!is_active ? "Activate" : "Deactivate"}
              checked={is_active}
              name={"is_active"}
              onChange={(e) => handleChange(e, 2)}
            />
            <ProtectedContent perms={["view_user"]}>
              <SelectCreator
                label={"USER"}
                classNamePrefix={"users"}
                id={"users"}
                closeMenuOnSelect={false}
                options={user_options}
                onChange={(e) => handleChange(e, 4)}
                value={users}
                large
                isMulti
              />
            </ProtectedContent>

            {/* Address Section */}
            <Col md="12">
              <PlacesAutocomplete
                value={street_address1}
                onChange={(addressKey) => {
                  setFormPayload((prev) => ({
                    ...prev,
                    address: {
                      ...(prev.address || initialAddress),
                      street_address1: addressKey,
                    },
                  }));
                }}
                onSelect={handleSelectAddress}
              >
                {({getInputProps, suggestions, getSuggestionItemProps, loading}) => (
                  <>
                    <FormGroup>
                      <label
                        className="form-control-label roboto-bold"
                        htmlFor="input-name"
                      >
                        Address
                      </label>
                      <AsteriskIcon />
                      <Input
                        {...getInputProps({
                          placeholder: "Type Address...",
                          className: "form-control form-control-alternative",
                        })}
                        data-cy="address_1"
                        invalid={isEmpty(street_address1) && submitted}
                      />
                    </FormGroup>
                    <ListGroup style={{position: "relative"}}>
                      <div className="autocomplete-dropdown-container">
                        {loading && <div className="mobilePart">Loading...</div>}
                        {suggestions.map((suggestion, index) => {
                          const className = suggestion.active
                            ? "suggestion-item--active"
                            : "suggestion-item";
                          const style = suggestion.active
                            ? {cursor: "pointer"}
                            : {backgroundColor: "#ffffff", cursor: "pointer"};
                          return (
                            <div
                              {...getSuggestionItemProps(suggestion, {
                                className,
                                style,
                              })}
                              key={index}
                            >
                              <ListGroupItem
                                className="borderBox"
                                data-cy="address-suggestion"
                              >
                                <div>{suggestion.description}</div>
                              </ListGroupItem>
                            </div>
                          );
                        })}
                      </div>
                    </ListGroup>
                  </>
                )}
              </PlacesAutocomplete>
            </Col>

            <Col md="6">
              <FormGroup>
                <label className="form-control-label" htmlFor="input-name">
                  City
                </label>
                <AsteriskIcon />
                <Input
                  className="form-control-alternative"
                  placeholder="City"
                  type="text"
                  name={"city"}
                  value={city}
                  onChange={(e) => handleChange(e, 3)}
                  data-cy="cy_City"
                  invalid={isEmpty(city) && submitted}
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <label className="form-control-label" htmlFor="input-name">
                  State
                </label>
                <AsteriskIcon />
                <Input
                  className="form-control-alternative"
                  placeholder="State"
                  type="text"
                  name={"state"}
                  value={state}
                  onChange={(e) => handleChange(e, 3)}
                  data-cy="cy_State"
                  invalid={isEmpty(state) && submitted}
                />
              </FormGroup>
            </Col>
            <SelectCreator
              isRequired
              label="Country"
              large
              name={"country"}
              onChange={(e) => handleChange(e, 3)}
              options={countryList}
              value={country}
              id="country"
              classNamePrefix={"country"}
            />
            <Col md="12">
              <FormGroup>
                <label className="form-control-label" htmlFor="input-name">
                  Postal Code
                </label>
                <AsteriskIcon />
                <Input
                  className="form-control-alternative"
                  placeholder="Postal Code"
                  type="text"
                  name={"postal_code"}
                  value={postal_code}
                  onChange={(e) => handleChange(e, 3)}
                  data-cy="cy_PostalCode"
                  invalid={isEmpty(postal_code) && submitted}
                />
              </FormGroup>
            </Col>
          </Row>
        </div>
      </ModalBody>
      <ModalFooter>
        {id && (
          <ProtectedContent perms={isSiteForm ? ["delete_site"] : ["delete_location"]}>
            <Button color={"danger"} onClick={() => handleDelete()} data-cy="delete-btn">
              {_Buttons.DELETE}
            </Button>
          </ProtectedContent>
        )}
        <ZeusButton onClick={handleSubmit} data-cy="submit-btn">
          {_Buttons.SAVE}
        </ZeusButton>
        <Button onClick={onClose} data-cy="cancel-btn">
          {_Buttons.CANCEL}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

CreateSite.propTypes = {
  data: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  type: PropTypes.oneOf(["site", "location"]).isRequired,
};

export default CreateSite;
